import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, TableBody, TableCell, TableHead, TableRow, Typography, TextField, Select, MenuItem, IconButton, TableSortLabel, InputAdornment, Stack, FormControl, InputLabel, Chip, useMediaQuery, useTheme, Card, CardContent, Popover } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import PropTypes from 'prop-types';
import { useUI } from '../Context/UIContext';
import TableLoadingOverlay from '../Shared/TableLoadingOverlay';
import AdminPageHeader, { adminPageShellSx } from '../Shared/AdminPageHeader';
import ScrollTopFab from '../Shared/ScrollTopFab';
import ActionIconButton from '../Shared/ActionIconButton';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { searchCategoriesPaged, deleteCategory } from '../../services/categoryServices';
import { getCategories } from '../../services/catalogServices';
import { usePermissions } from '../Context/permissionsProvider';
import InsertCategoryDialog from './InsertCategoryDialog';
import ImportCsvDialog from './ImportCsvDialog';
import DettaglioCategoriaModal from './DettaglioCategoriaModal';
import AdminMergedTable from '../Shared/AdminMergedTable';
import AdminTablePagination from '../Shared/AdminTablePagination';
import { mergedTableHeadSx, mergedTableRowSx } from '../../utils/adminTableScrollSx';

// --- Sotto-componenti per ridurre la complessità ---

const CategoryMobileCard = ({ cat, parentCategories, onEdit, onDelete }) => {
  const { isActionVisible } = usePermissions();

  return (
  <Card
    elevation={0}
    sx={{
      borderRadius: "18px",
      border: "1px solid #e6ebf3",
      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
      overflow: "hidden"
    }}
  >
    <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
      <Box display="flex" gap={1.5} alignItems="center">
        {/* Tile monogramma categoria */}
        <Box sx={{
          width: 46,
          height: 46,
          flexShrink: 0,
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#eef4ff",
          color: "#0b3d87",
          border: "1px solid #dbe7fb",
          fontWeight: 800,
          fontSize: "1.15rem"
        }}>
          {(cat.nome || "?").trim().charAt(0).toUpperCase() || "?"}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", lineHeight: 1.25, wordBreak: "break-word" }}>
            {cat.nome}
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.68rem", display: "block", mt: 0.25 }}>
            ID {cat.categoryId} · Padre: {cat.parentId ? parentCategories.find(p => p.categoryId === cat.parentId)?.nome || cat.parentId : '-'}
          </Typography>
        </Box>
        <Typography sx={{ fontWeight: 800, color: "#0b3d87", fontSize: "0.95rem", flexShrink: 0 }}>
          {cat.prezzoPpc ? `${cat.prezzoPpc.toFixed(2)} €` : '-'}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5} pt={1.25} sx={{ borderTop: "1px solid #f0f2f5" }}>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={cat.isDeleted === 0 ? "Attiva" : "Inattiva"}
            size="small"
            sx={{
              height: 24,
              fontWeight: 700,
              fontSize: "0.65rem",
              backgroundColor: cat.isDeleted === 0 ? "#ecfdf5" : "#fef2f2",
              color: cat.isDeleted === 0 ? "#10b981" : "#ef4444",
              border: `1px solid ${cat.isDeleted === 0 ? "#d1fae5" : "#fee2e2"}`,
              borderRadius: "6px"
            }}
          />
        </Box>
        <Box>
          {isActionVisible("gestioneCategorie", "modifica") && (
            <ActionIconButton
              title="Modifica/Dettaglio"
              onClick={() => onEdit(cat)}
              icon={EditIcon}
              color="primary"
            />
          )}
          {isActionVisible("gestioneCategorie", "elimina") && (
            <ActionIconButton
              title="Elimina"
              onClick={() => onDelete(cat.categoryId)}
              icon={DeleteIcon}
              color="#f44336"
              disabled={cat.isDeleted === 1}
            />
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
  );
};

CategoryMobileCard.propTypes = {
  cat: PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nome: PropTypes.string,
    parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prezzoPpc: PropTypes.number,
    isDeleted: PropTypes.number
  }).isRequired,
  parentCategories: PropTypes.arrayOf(PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nome: PropTypes.string
  })),
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

const CategoryDesktopTable = ({
  categories, parentCategories, loading,
  orderBy, order, handleRequestSort, onEdit, onDelete, search, headerTop
}) => {
  const { isActionVisible } = usePermissions();

  return (
  <AdminMergedTable
    tableLoading={loading}
    head={(
      <TableHead sx={mergedTableHeadSx(headerTop)}>
        <TableRow>
          {[
            { id: 'category_id', label: 'ID' },
            { id: 'nome', label: 'Nome Categoria' },
            { id: 'parent', label: 'Categoria Padre', sortable: false },
            { id: 'stato', label: 'Stato', sortable: false },
            { id: 'prezzo_ppc', label: 'PPC (€)' },
            { id: 'azioni', label: 'Azioni', sortable: false, align: 'center' }
          ].map((col) => (
            <TableCell key={col.id} sx={{ textAlign: col.align || 'left' }}>
              {col.sortable === false ? col.label : (
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order.toLowerCase() : 'asc'}
                  onClick={() => handleRequestSort(col.id)}
                  sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  {col.label}
                </TableSortLabel>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    )}
    body={(
      <TableBody>
        {Array.isArray(categories) && categories.map((cat, index) => (
          <TableRow key={cat.categoryId} sx={mergedTableRowSx(index)}>
            <TableCell fontWeight="bold">{cat.categoryId}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{cat.nome}</TableCell>
            <TableCell>
              {cat.parentId ? parentCategories.find(p => p.categoryId === cat.parentId)?.nome || cat.parentId : '-'}
            </TableCell>
            <TableCell>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  backgroundColor: cat.isDeleted === 0 ? '#ecfdf5' : '#fef2f2',
                  color: cat.isDeleted === 0 ? '#10b981' : '#ef4444',
                  border: `1px solid ${cat.isDeleted === 0 ? '#d1fae5' : '#fee2e2'}`
                }}
              >
                {cat.isDeleted === 0 ? "Attiva" : "Inattiva"}
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#0b3d87' }}>
              {cat.prezzoPpc ? `${cat.prezzoPpc.toFixed(2)} €` : '-'}
            </TableCell>
            <TableCell sx={{ textAlign: 'center' }}>
              {isActionVisible("gestioneCategorie", "modifica") && (
                <ActionIconButton
                  title="Modifica/Dettaglio"
                  onClick={() => onEdit(cat)}
                  icon={EditIcon}
                  color="primary"
                />
              )}
              {isActionVisible("gestioneCategorie", "elimina") && (
                <ActionIconButton
                  title="Elimina"
                  onClick={() => onDelete(cat.categoryId)}
                  icon={DeleteIcon}
                  color="#f44336"
                  disabled={cat.isDeleted === 1}
                />
              )}
            </TableCell>
          </TableRow>
        ))}
        {categories.length === 0 && !loading && (
          <TableRow>
            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
              {search.length > 0 && search.length < 3 ? "Inserisci almeno 3 caratteri per cercare." : "Nessuna categoria trovata."}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    )}
  />
  );
};

CategoryDesktopTable.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nome: PropTypes.string,
    parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prezzoPpc: PropTypes.number,
    isDeleted: PropTypes.number
  })).isRequired,
  parentCategories: PropTypes.arrayOf(PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nome: PropTypes.string
  })),
  loading: PropTypes.bool.isRequired,
  orderBy: PropTypes.string.isRequired,
  order: PropTypes.string.isRequired,
  handleRequestSort: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  headerTop: PropTypes.number
};

const CategoryPagination = ({ totalElements, rowsPerPage, setRowsPerPage, page, setPage }) => (
  <AdminTablePagination
    rowsPerPage={rowsPerPage}
    setRowsPerPage={setRowsPerPage}
    page={page}
    setPage={setPage}
    totalPages={Math.ceil(totalElements / rowsPerPage) || 1}
    countLabel="Categorie"
    countValue={totalElements}
    rowsOptions={[5, 10, 25, 50]}
  />
);

CategoryPagination.propTypes = {
  totalElements: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired
};

const GestioneCategorie = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [categories, setCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);

  // Paginazione e Ordinamento
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('nome');
  const [order, setOrder] = useState('ASC');

  // Filtri
  const [search, setSearch] = useState('');
  const [filtroPadre, setFiltroPadre] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [minPpc, setMinPpc] = useState('');
  const [maxPpc, setMaxPpc] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Il dropdown si ancora all'intera barra di ricerca (non al pulsante), cosi'
  // resta allineato e largo quanto lei.
  const searchBarRef = useRef(null);

  const handleToggleFilters = () => {
    setShowAdvancedFilters((open) => !open);
  };

  const handleCloseFilters = () => {
    setShowAdvancedFilters(false);
  };

  const hasActiveFilters = search.length >= 3 || filtroPadre !== '' || filtroStato !== '' || minPpc !== '' || maxPpc !== '';

  // Modali
  const [insertOpen, setInsertOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { showSuccess } = useUI();
  const lastParamsRef = useRef(null);

  const fetchCategories = useCallback(async (currentParams) => {
    // Gestione del caso in cui currentParams non è passato (es. dopo delete)
    const paramsToUse = currentParams || lastParamsRef.current || {
      search, filtroPadre, filtroStato, minPpc, maxPpc, page, rowsPerPage, orderBy, order
    };

    setLoading(true);
    try {
      const { search: s, filtroPadre: fPadre, filtroStato: fStato, minPpc: minP, maxPpc: maxP, page: p, rowsPerPage: rpp, orderBy: ob, order: o } = paramsToUse;
      const request = {
        testoRicerca: s,
        parentId: fPadre || null,
        isDeleted: fStato === '' ? null : Number.parseInt(fStato, 10),
        prezzoMin: minP ? Number.parseFloat(minP) : null,
        prezzoMax: maxP ? Number.parseFloat(maxP) : null,
        numeroPagina: p,
        dimensionePagina: rpp,
        ordinamento: [{ campo: ob, direzione: o }]
      };
      const data = await searchCategoriesPaged(request);
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [search, filtroPadre, filtroStato, minPpc, maxPpc, page, rowsPerPage, orderBy, order]);

  // Effect unico per caricamento dati
  useEffect(() => {
    // Carica categorie padre se non presenti
    if (parentCategories.length === 0) {
      getCategories("").then(data => setParentCategories(Array.isArray(data) ? data : [])).catch(err => console.error("Error loading parent categories:", err));
    }

    const handler = setTimeout(() => {
      if (search.length > 0 && search.length < 3) return;

      const currentParams = {
        search, filtroPadre, filtroStato, minPpc, maxPpc, page, rowsPerPage, orderBy, order
      };

      const lastParams = lastParamsRef.current;
      if (lastParams && JSON.stringify(currentParams) === JSON.stringify(lastParams)) return;

      const filtersChanged = lastParams && (
        search !== lastParams.search ||
        filtroPadre !== lastParams.filtroPadre ||
        filtroStato !== lastParams.filtroStato ||
        minPpc !== lastParams.minPpc ||
        maxPpc !== lastParams.maxPpc
      );

      if (filtersChanged && page !== 0) {
        setPage(0);
      } else {
        fetchCategories(currentParams);
        lastParamsRef.current = currentParams;
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [fetchCategories, parentCategories.length, search, filtroPadre, filtroStato, minPpc, maxPpc, page, rowsPerPage, orderBy, order]);




  const handleClearFilters = () => {
    setSearch('');
    setFiltroPadre('');
    setFiltroStato('');
    setMinPpc('');
    setMaxPpc('');
    setPage(0);
    handleCloseFilters();
  };

  // Pulsanti di pagina: impilati e centrati tra il titolo e la barra di ricerca.
  const pageActionButtons = (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadFileIcon />}
        onClick={() => setImportOpen(true)}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', minWidth: 190 }}
      >
        Importa CSV
      </Button>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setInsertOpen(true)}
        sx={{ backgroundColor: '#0b3d87', borderRadius: 2, textTransform: 'none', fontWeight: 'bold', minWidth: 190 }}
      >
        Nuova Categoria
      </Button>
    </>
  );

  const searchAndFilters = (
    <Box ref={searchBarRef} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'nowrap', width: { xs: '100%', md: 'auto' } }}>
      <TextField
        placeholder="Cerca categoria (min. 3 caratteri)..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ flex: { xs: 1, md: '0 0 auto' }, width: { xs: 'auto', md: 320 }, minWidth: 0 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#0b3d87', opacity: 0.6 }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '16px',
              backgroundColor: '#f8fafc',
              height: '54px',
              fontSize: '0.9rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { backgroundColor: '#f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' },
              '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 4px rgba(11, 61, 135, 0.08), 0 4px 12px rgba(11, 61, 135, 0.05)', '& fieldset': { borderColor: '#0b3d87 !important', borderWidth: '2px' } },
              '& fieldset': { borderColor: '#e2e8f0 !important' }
            }
          },
        }}
      />
      <Tooltip title="Filtri Avanzati" copyable={false}>
        <IconButton
          onClick={handleToggleFilters}
          sx={{
            flexShrink: 0,
            backgroundColor: showAdvancedFilters ? '#0b3d87' : 'rgba(0, 0, 0, 0.04)',
            color: showAdvancedFilters ? 'white' : '#475569',
            borderRadius: '16px',
            width: '54px',
            height: '54px',
            transition: 'all 0.3s ease',
            '&:hover': { backgroundColor: showAdvancedFilters ? '#082d63' : 'rgba(0, 0, 0, 0.08)' }
          }}
        >
          {showAdvancedFilters ? <ExpandLessIcon /> : <TuneIcon />}
        </IconButton>
      </Tooltip>
      {hasActiveFilters && (
        <Tooltip title="Pulisci filtri" copyable={false}>
          <IconButton
            onClick={handleClearFilters}
            sx={{
              flexShrink: 0,
              backgroundColor: '#fee2e2',
              color: '#ef4444',
              borderRadius: '16px',
              width: '54px',
              height: '54px',
              transition: 'all 0.3s ease',
              '&:hover': { backgroundColor: '#fecaca' }
            }}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const handleDelete = async (id) => {
    if (globalThis.confirm("Sei sicuro di voler eliminare questa categoria? I prodotti collegati verranno disattivati.")) {
      try {
        await deleteCategory(id);
        showSuccess("Categoria eliminata con successo!");
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  // Validazione PPC Range
  const isPpcRangeInvalid = minPpc && maxPpc && Number.parseFloat(minPpc) > Number.parseFloat(maxPpc);
  const onEdit = (cat) => { setSelectedCategory(cat); setDetailOpen(true); };

  return (
    <Box sx={adminPageShellSx}>
      <AdminPageHeader
        title="Gestione Categorie"
        subtitle="Organizza e gestisci l'albero delle categorie del catalogo."
        centerActions={pageActionButtons}
        actions={isMobile ? undefined : searchAndFilters}
      />

      {isMobile && (
        <Box
          sx={{
            position: 'sticky',
            top: 15,
            zIndex: 1100,
            mb: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(16px) saturate(180%)',
            WebkitBackdropFilter: 'blur(16px) saturate(180%)',
            borderRadius: '18px',
            p: 1,
            boxShadow: '0 8px 24px -10px rgba(31, 38, 135, 0.18)',
          }}
        >
          {searchAndFilters}
        </Box>
      )}

      <Popover
        open={showAdvancedFilters}
        anchorEl={searchBarRef.current}
        onClose={handleCloseFilters}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: searchBarRef.current ? searchBarRef.current.clientWidth : 'auto',
              mt: 1,
              p: 2,
              boxSizing: 'border-box',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(11, 61, 135, 0.15)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              maxHeight: '60vh',
              overflowX: 'hidden'
            }
          }
        }}
      >
        <Stack spacing={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ color: '#64748b', fontSize: '0.85rem' }}>Categoria Padre</InputLabel>
            <Select
              value={filtroPadre}
              label="Categoria Padre"
              onChange={(e) => setFiltroPadre(e.target.value)}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                fontSize: '0.85rem',
                '&:hover': { backgroundColor: '#f1f5f9' },
                '&.Mui-focused': { backgroundColor: 'white', '& fieldset': { borderColor: '#0b3d87 !important' } }
              }}
            >
              <MenuItem value="">Tutte</MenuItem>
              {Array.isArray(parentCategories) && parentCategories.map(cat => (
                <MenuItem key={cat.categoryId} value={cat.categoryId}>{cat.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel sx={{ color: '#64748b', fontSize: '0.85rem' }}>Stato</InputLabel>
            <Select
              value={filtroStato}
              label="Stato"
              onChange={(e) => setFiltroStato(e.target.value)}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                fontSize: '0.85rem',
                '&:hover': { backgroundColor: '#f1f5f9' },
                '&.Mui-focused': { backgroundColor: 'white', '& fieldset': { borderColor: '#0b3d87 !important' } }
              }}
            >
              <MenuItem value="">Tutti</MenuItem>
              <MenuItem value="0">Attiva</MenuItem>
              <MenuItem value="1">Inattiva/Eliminata</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Min PPC (€)"
              fullWidth
              type="number"
              value={minPpc}
              onChange={(e) => setMinPpc(e.target.value)}
              error={isPpcRangeInvalid}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              slotProps={{
                input: {
                  sx: { borderRadius: '12px', backgroundColor: '#f8fafc', fontSize: '0.85rem' }
                }
              }}
            />
            <TextField
              label="Max PPC (€)"
              fullWidth
              type="number"
              value={maxPpc}
              onChange={(e) => setMaxPpc(e.target.value)}
              error={isPpcRangeInvalid}
              helperText={isPpcRangeInvalid ? 'Min > Max' : ''}
              size="small"
              InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
              slotProps={{
                input: {
                  sx: { borderRadius: '12px', backgroundColor: '#f8fafc', fontSize: '0.85rem' }
                }
              }}
            />
          </Stack>
        </Stack>
      </Popover>

      <Box sx={{ position: 'relative', minHeight: '300px' }}>
        <TableLoadingOverlay loading={loading} />

        {isMobile ? (
          <Stack spacing={2} sx={{ mb: 4, minHeight: '300px' }}>
            {categories.length === 0 && !loading ? (
              <Typography textAlign="center" color="text.secondary" py={3}>
                {search.length > 0 && search.length < 3 ? "Inserisci almeno 3 caratteri per cercare." : "Nessuna categoria trovata."}
              </Typography>
            ) : (
              categories.map((cat) => (
                <CategoryMobileCard key={cat.categoryId} cat={cat} parentCategories={parentCategories} onEdit={onEdit} onDelete={handleDelete} />
              ))
            )}
          </Stack>
        ) : (
          <CategoryDesktopTable
            categories={categories} parentCategories={parentCategories} loading={loading}
            orderBy={orderBy} order={order} handleRequestSort={handleRequestSort}
            onEdit={onEdit} onDelete={handleDelete} search={search}
            headerTop={0}
          />
        )}
      </Box>

      <CategoryPagination
        totalElements={totalElements}
        rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
        page={page} setPage={setPage}
      />

      <ScrollTopFab />

      <InsertCategoryDialog
        open={insertOpen}
        onClose={() => setInsertOpen(false)}
        onSuccess={() => { setInsertOpen(false); fetchCategories(); showSuccess("Categoria aggiunta!"); }}
        parentCategories={parentCategories}
      />

      <ImportCsvDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => { setImportOpen(false); fetchCategories(); showSuccess("Importazione completata!"); }}
      />

      {selectedCategory && (
        <DettaglioCategoriaModal
          open={detailOpen}
          handleClose={() => setDetailOpen(false)}
          categoria={selectedCategory}
          onUpdate={() => { setDetailOpen(false); fetchCategories(); }}
          parentCategories={parentCategories}
        />
      )}
    </Box>
  );
};

export default GestioneCategorie;
